import React, { useState } from 'react';
import { LogOut, Clock, UserCheck, Phone, User, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FeedbackBanner } from '../../components/ui/PageLayout';
import ImagePreviewModal from '../../components/ImagePreviewModal';
import api, { getAssetUrl, getApiErrorMessage } from '../../services/api';

const VisitantesTable = ({ visitantes, loading, refreshData, onEdit }) => {
  const [imagemPreview, setImagemPreview] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const handleRegisterExit = async (id) => {
    if (window.confirm("Confirmar a saída deste visitante?")) {
      try {
        const { data } = await api.patch(`/visitantes/${id}/saida`);
        setFeedback({ type: "success", message: data.message });
        refreshData();
      } catch (error) {
        setFeedback({ type: "error", message: getApiErrorMessage(error, "Erro ao registrar saída.") });
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Deseja excluir este visitante?")) {
      try {
        await api.delete(`/visitantes/${id}`);
        refreshData();
      } catch (error) {
        setFeedback({ type: "error", message: getApiErrorMessage(error, "Erro ao excluir visitante.") });
      }
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-500 font-medium">Carregando visitantes...</div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="max-h-[600px] overflow-y-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm text-gray-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-4 font-semibold">Visitante</th>
              <th className="px-6 py-4 font-semibold">Contato</th> {/* NOVA COLUNA */}
              <th className="px-6 py-4 font-semibold">Responsável</th>
              <th className="px-6 py-4 font-semibold text-center">Entrada</th>
              <th className="px-6 py-4 font-semibold text-center">Saída</th>
              <th className="px-6 py-4 font-semibold text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visitantes.map((v) => (
              <tr key={v.id} className="hover:bg-gray-50/80 transition-colors group">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 overflow-hidden rounded-full bg-slate-100 flex items-center justify-center">
                      {v.foto_url ? (
                        <button
                          type="button"
                          onClick={() => setImagemPreview({ src: getAssetUrl(v.foto_url), alt: v.nome_completo })}
                          className="h-full w-full cursor-zoom-in"
                          title="Visualizar foto"
                        >
                          <img src={getAssetUrl(v.foto_url)} alt={v.nome_completo} className="h-full w-full object-cover" />
                        </button>
                      ) : (
                        <User size={18} className="text-slate-400" />
                      )}
                    </div>
                    <button type="button" onClick={() => onEdit(v)} className="text-left font-medium text-slate-700 transition-colors hover:text-blue-600 hover:underline" title="Editar visitante">{v.nome_completo}</button>
                  </div>
                </td>
                {/* EXIBIÇÃO DO CONTATO */}
                <td className="px-6 py-4 text-gray-500 text-sm">
                  <div className="flex items-center gap-2">
                    {v.contato ? (
                       <a 
                         href={`https://wa.me/55${v.contato.replace(/\D/g, '')}`} 
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="flex items-center gap-2 text-green-600 hover:text-green-700 font-medium transition-colors"
                         title="Chamar no WhatsApp"
                        >
                        <Phone size={14} />
                        {v.contato}
                      </a>
                    ) : (
                      <span className="text-gray-300 italic">Não informado</span>
                    )}
                  </div>
                </td>

                <td className="px-6 py-4 text-gray-500 text-sm">
                  <span className="font-semibold text-slate-600">{v.nome_morador || "---"}</span>
                </td>
                
                <td className="px-6 py-4 text-center">
                  <div className="flex flex-col items-center text-xs text-gray-500">
                    <Clock size={12} className="mb-1 text-blue-400" />
                    {format(new Date(v.data_entrada), "dd/MM - HH:mm", { locale: ptBR })}
                  </div>
                </td>

                <td className="px-6 py-4 text-center">
                  {v.data_saida ? (
                    <div className="flex flex-col items-center text-xs text-gray-400 italic">
                      {format(new Date(v.data_saida), "dd/MM - HH:mm", { locale: ptBR })}
                    </div>
                  ) : (
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-full font-bold animate-pulse">
                      No Local
                    </span>
                  )}
                </td>

                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleDelete(v.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      title="Excluir visitante"
                    >
                      <Trash2 size={18} />
                    </button>
                    {!v.data_saida && (
                      <button 
                        onClick={() => handleRegisterExit(v.id)}
                        title="Registrar Saída"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all text-xs font-bold"
                      >
                        <LogOut size={16} />
                        Registrar Saída
                      </button>
                    )}
                    {v.data_saida && (
                      <UserCheck size={18} className="my-auto text-gray-300" title="Visita Finalizada" />
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {visitantes.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-20 text-center text-gray-400 italic">
                  Nenhum registro de visita encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <FeedbackBanner type={feedback?.type} message={feedback?.message} onClose={() => setFeedback(null)} />
      <ImagePreviewModal image={imagemPreview} onClose={() => setImagemPreview(null)} />
    </div>
  );
};

export default VisitantesTable;
